import { useNavigate } from 'react-router-dom';
import { Rocket, PanelLeft } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { PanelHeader } from '@reflex/ui/components/layout/PanelHeader';
import { PanelTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { useOrgRoutes } from '@/lib/useOrgRoutes';

interface EmptyInboxProps {
  onToggleSidebar?: () => void;
  /**
   * Optional callback invoked when the user clicks "Launch an agent". When
   * provided, the empty state opens an inline launch dialog instead of
   * navigating to the agents page.
   */
  onLaunchAgent?: () => void;
}

export function EmptyInbox({ onToggleSidebar, onLaunchAgent }: EmptyInboxProps) {
  const routes = useOrgRoutes();
  const navigate = useNavigate();

  const handleLaunch = () => {
    if (onLaunchAgent) {
      onLaunchAgent();
    } else {
      navigate(`${routes.runs}?new=true`);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PanelHeader
        left={
          onToggleSidebar ? (
            <Button
              variant="ghost"
              size="icon-sm"
              className="shrink-0 md:hidden"
              onClick={onToggleSidebar}
              aria-label="Open sidebar"
            >
              <PanelLeft className="h-4 w-4" />
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 text-center">
        <button
          type="button"
          onClick={handleLaunch}
          aria-label="Launch an agent"
          data-testid="inbox-empty-launch-agent-button"
          className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Rocket className="h-7 w-7" />
        </button>
        <div>
          <PanelTitle as="h2">
            <button
              type="button"
              onClick={handleLaunch}
              className="cursor-pointer rounded-sm underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Launch an agent
            </button>
          </PanelTitle>
          <PageDescription className="max-w-sm">
            Describe a task and let an agent handle it. Anything that needs your input or fails
            along the way lands here for you to triage.
          </PageDescription>
        </div>
      </div>
    </div>
  );
}
